import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Activity } from 'lucide-react';
import { SEO } from '../components/SEO';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';

export const Services = () => {
  const services = [
    {
      title: 'Leg & Foot Care Interventions',
      path: '/services/leg-foot-care',
      description: 'Peripheral artery disease (PAD), varicose veins, deep vein thrombosis (DVT), and diabetic foot salvage',
      treatments: ['Angioplasty & Stenting', 'EVLT (Laser Treatment)', 'VenaSeal Glue Therapy', 'Sclerotherapy', 'IVC Filter', 'Below-knee Angioplasty'],
      icon: '🦵'
    },
    {
      title: 'Liver & GI Interventions',
      path: '/services/liver-gi',
      description: 'Portal hypertension, biliary obstruction, liver abscess drainage, liver cancer treatment, and mesenteric ischaemia',
      treatments: ['TACE (Chemoembolization)', 'TARE (Y-90 Radioembolization)', 'TIPS Procedure', 'PTBD & Biliary Stenting', 'Microwave Ablation', 'Liver Abscess Drainage'],
      icon: '🫀'
    },
    {
      title: 'Kidney & Dialysis Access',
      path: '/services/kidney-dialysis',
      description: 'AV fistula procedures, urinary obstruction management, and renal artery interventions',
      treatments: ['AV Fistula Angioplasty', 'Fistula Declotting', 'PCN (Percutaneous Nephrostomy)', 'Ureteric Stenting', 'Renal Artery Angioplasty', 'Permcath Insertion'],
      icon: '🫘'
    },
    {
      title: 'Women\'s Health Interventions',
      path: '/services/womens-health',
      description: 'Uterine artery embolization for fibroids and ovarian vein embolization for pelvic congestion',
      treatments: ['UAE for Uterine Fibroids', 'Ovarian Vein Embolization', 'Postpartum Hemorrhage Treatment'],
      icon: '♀️'
    },
    {
      title: 'Emergency Interventions',
      path: '/services/emergency',
      description: 'Life-saving emergency vascular interventions for uncontrolled bleeding',
      treatments: ['Trauma Embolization', 'GI Bleeding Control', 'Postpartum Hemorrhage', 'EVAR (Aortic Aneurysm)', 'Hemoptysis Treatment', 'Hematuria Management'],
      icon: '🚨'
    },
    {
      title: 'Miscellaneous Procedures',
      path: '/services/miscellaneous',
      description: 'Specialized interventional procedures for various conditions',
      treatments: ['PICC Line Insertion', 'Permcath Insertion', 'Varicocele Embolization', 'Vascular Malformation Treatment'],
      icon: '➕'
    }
  ];

  const benefits = [
    'No Major Surgery - done through 2-3mm skin puncture',
    'No General Anaesthesia (Usually) - local anaesthesia and light sedation',
    'Day-Care or One-Night Stay - minimal hospitalisation',
    'Faster Recovery - return to work in days, not weeks',
    'Millimetre Precision - imaging-guided accuracy',
    'Safe for High-Risk Patients - elderly and medically complex patients'
  ];

  return (
    <>
      <SEO 
        title="Interventional Radiology Services in Mumbai | Dr. Punit Mahajan"
        description="Dr. Punit Mahajan offers minimally invasive interventional radiology services in Mumbai for vascular disease, liver cancer, kidney conditions, uterine fibroids, dialysis access, and emergency haemorrhage."
      />

      {/* Hero */}
      <section className="bg-gradient-to-br from-blue-950 to-slate-900 text-white py-20 relative overflow-hidden"><div className="absolute inset-0 pattern-hero opacity-20"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <Activity className="w-16 h-16 text-amber-500 mx-auto mb-6" />
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 animate-text-reveal">
            Interventional Radiology Services in Mumbai
          </h1>
          <p className="text-xl md:text-2xl text-slate-300 max-w-3xl mx-auto animate-text-reveal" style={{ animationDelay: "300ms" }}>
            The Full Spectrum of Minimally Invasive Care
          </p>
        </div>
      </section>

      {/* What is IR */}
      <section className="py-16 bg-cream-textured relative overflow-hidden">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-6">
            <div className="gold-ornament">Introduction</div>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6 text-center">
            What is Interventional Radiology?
          </h2>
          <p className="text-lg text-slate-600 leading-relaxed mb-6">
            Interventional Radiology (IR) is a medical subspecialty that uses real-time imaging — X-ray fluoroscopy, ultrasound, and CT — to guide catheters, wires, and precision instruments through tiny skin punctures to treat conditions deep inside the body.
          </p>
          <p className="text-lg text-slate-600 leading-relaxed">
            Unlike traditional surgery, IR requires no large incisions, usually no general anaesthesia, and allows patients to recover in days rather than weeks. Dr. Punit Mahajan, Interventional Radiologist in Mumbai, performs a comprehensive range of IR procedures across six clinical categories.
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 bg-cream-stripes relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="gold-ornament mb-4">Services</div>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Our Specialized Services
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <Card key={index} className="card-gold-hover bg-white">
                <CardContent className="p-8">
                  <div className="text-5xl mb-4">{service.icon}</div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-3">{service.title}</h3>
                  <p className="text-slate-600 mb-6 leading-relaxed">{service.description}</p>
                  
                  <div className="mb-6">
                    <h4 className="font-semibold text-slate-900 mb-3">Treatments Include:</h4>
                    <div className="space-y-2">
                      {service.treatments.map((treatment, idx) => (
                        <div key={idx} className="flex items-start">
                          <CheckCircle className="w-5 h-5 text-amber-600 mr-2 mt-0.5 flex-shrink-0" />
                          <span className="text-slate-600 text-sm">{treatment}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <Link to={service.path}>
                    <Button className="w-full bg-blue-900 hover:bg-blue-800 text-white">
                      Learn More <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 bg-cream-pattern relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="gold-ornament mb-4">Benefits</div>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Benefits of Interventional Radiology Over Open Surgery
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {benefits.map((benefit, index) => (
              <div key={index} className="flex items-start space-x-3 bg-white p-6 rounded-lg border-l-4 border-amber-500 shadow-sm hover:shadow-md transition-shadow">
                <CheckCircle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-1" />
                <span className="text-slate-700 font-medium">{benefit}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-br from-blue-900 to-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-6">
          <h2 className="text-3xl md:text-4xl font-bold">
            Find the Right Treatment for Your Condition
          </h2>
          <p className="text-xl text-slate-300">
            Book a consultation to discuss your options with Dr. Punit Mahajan
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-amber-600 hover:bg-amber-700 text-white text-lg px-8 py-6">
              Book Appointment <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </>
  );
};
